import React from 'react';
import { Box, Grid, Paper, Select, TextField, Typography } from '@mui/material';

export const CUSTOMER_INPUT_MIN_HEIGHT = 32;
export const CUSTOMER_INPUT_ICON_SIZE = 16;

export function cx(...classes) {
    return classes.filter(Boolean).join(' ');
}

export function CustomerFieldLabel({ label, required, labelExtra, centered = false, className = '' }) {
    return (
        <Typography
            component="label"
            className={cx(
                'customer-form-field-label',
                centered && 'customer-form-field-label--center',
                className
            )}
        >
            <span>
                {label}
                {required ? (
                    <Box component="span" className="customer-form-required">
                        *
                    </Box>
                ) : null}
            </span>
            {labelExtra}
        </Typography>
    );
}

export function CustomerFormSection({ title, subtitle, icon: Icon, children }) {
    return (
        <Paper elevation={0} className="customer-form-section">
            <Box className="customer-form-section-header">
                {Icon ? (
                    <Box className="customer-form-icon-badge">
                        <Icon className="customer-form-icon" />
                    </Box>
                ) : null}
                <Box>
                    <Typography className="customer-form-section-title">
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography className="customer-form-section-subtitle">
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            <Grid container spacing={{ xs: 1, md: 1.5 }}>
                {children}
            </Grid>
        </Paper>
    );
}

export function CustomerFormField({ label, required, error, helper, fullWidth, mdCols, labelExtra, highlight, children }) {
    const gridMd = mdCols ?? (fullWidth ? 12 : 6);
    return (
        <Grid item xs={12} md={gridMd}>
            <Box className={highlight ? 'customer-form-highlight' : undefined}>
                <CustomerFieldLabel label={label} required={required} labelExtra={labelExtra} />
                {children}
                {error ? (
                    <Typography component="div" className="danger-color error-msg customer-form-field-error">
                        {error}
                    </Typography>
                ) : null}
                {helper ? (
                    <Typography className="customer-form-field-helper">
                        {helper}
                    </Typography>
                ) : null}
            </Box>
        </Grid>
    );
}

export function CustomerBorderedInput({
    value,
    onChange,
    onKeyPress,
    onKeyDown,
    placeholder,
    inputProps = {},
    disabled = false,
    icon: Icon,
    suffix,
    error = false,
    className = '',
    style,
}) {
    return (
        <Box
            className={cx(
                'customer-form-field-wrap',
                error && 'customer-form-field-wrap--error',
                disabled && 'customer-form-field-wrap--disabled',
                className
            )}
            style={style}
        >
            {Icon ? <Icon className="customer-form-field-icon" /> : null}
            <TextField
                type="text"
                fullWidth
                size="small"
                margin="none"
                variant="standard"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onKeyPress={onKeyPress}
                onKeyDown={onKeyDown}
                disabled={disabled}
                inputProps={inputProps}
                InputProps={{ disableUnderline: true }}
                className="customer-form-field-input"
            />
            {suffix ? (
                <Typography className="customer-form-field-suffix">
                    {suffix}
                </Typography>
            ) : null}
        </Box>
    );
}

export function CustomerBorderedSelect({ value, onChange, children, error = false, disabled = false, className = '' }) {
    return (
        <Box
            className={cx(
                'customer-form-field-wrap',
                error && 'customer-form-field-wrap--error',
                disabled && 'customer-form-field-wrap--disabled',
                className
            )}
        >
            <Select
                fullWidth
                variant="standard"
                disableUnderline
                value={value}
                onChange={onChange}
                disabled={disabled}
                className="customer-form-field-input"
            >
                {children}
            </Select>
        </Box>
    );
}

export function CustomerBorderedTextarea({
    value,
    onChange,
    onKeyPress,
    placeholder,
    rows = 6,
    error = false,
    className = '',
    textareaProps = {},
    icon: Icon,
}) {
    return (
        <Box
            className={cx(
                'customer-form-field-wrap',
                'customer-form-field-wrap--textarea',
                error && 'customer-form-field-wrap--error',
                className
            )}
        >
            {Icon ? <Icon className="customer-form-field-icon customer-form-field-icon--top" /> : null}
            <textarea
                rows={rows}
                aria-label={placeholder}
                className="text-justify cursor-auto"
                value={value}
                onChange={onChange}
                onKeyPress={onKeyPress}
                placeholder={placeholder}
                {...textareaProps}
            />
        </Box>
    );
}
